import { useEffect, useLayoutEffect } from "react";

import { useLoaderData } from "react-router";

import { signHs256Jwt } from "@utils/jwt";

import { ENV_CONFIG } from "@configs/env";

const CHAT_WIDGET_SCRIPT_URL = "https://chat-api.spartez-software.com/chat-widget.js";
const JIRA_ID = "4a414dbb-1632-3e58-84d0-c20f1a140c17";
const SERVICE_DESK_ID = "1";
const PRIMARY_COLOR = "#2787fa";
const TEXT_COLOR = "#FFFFFF";

interface ChatWidgetElement extends HTMLElement {
	loginWithToken: (token: string) => Promise<void>;
	show: () => void;
}

const useChatWidgetScript = () => {
	useLayoutEffect(() => {
		const chatApiScript = document.createElement("script");
		chatApiScript.src = CHAT_WIDGET_SCRIPT_URL;
		chatApiScript.defer = true;
		document.body.appendChild(chatApiScript);
		return () => {
			document.body.removeChild(chatApiScript);
		};
	}, []);
};

const appendChatWidgetStyles = () => {
	const chatWidgetStyle = document.createElement("style");
	chatWidgetStyle.innerHTML = `
		chat-widget::part(wrapper) {
			color: #000;
		}
		chat-widget::part(authentication-section) {
			display: none !important;
		}
	`;
	document.body.appendChild(chatWidgetStyle);
	return () => {
		document.body.removeChild(chatWidgetStyle);
	};
};

const setBaseAttributes = (el: HTMLElement) => {
	el.setAttribute("jira-id", JIRA_ID);
	el.setAttribute("service-desk-id", SERVICE_DESK_ID);
	el.setAttribute("primary-color", PRIMARY_COLOR);
	el.setAttribute("text-color", TEXT_COLOR);
};

/** For unauthorized users (e.g. login page). */
export const ChatWidgetPublic = () => {
	const environment = ENV_CONFIG.PROJECT_NAME_ENV;

	useChatWidgetScript();

	useEffect(() => {
		const chatWidgetScript = document.createElement("chat-widget");
		setBaseAttributes(chatWidgetScript);
		chatWidgetScript.setAttribute(
			"metadata",
			`[
				{ name: "Environment", value: "${environment}", render: true }
			]`,
		);

		document.body.appendChild(chatWidgetScript);
		const removeStyles = appendChatWidgetStyles();

		return () => {
			document.body.removeChild(chatWidgetScript);
			removeStyles();
		};
	}, [environment]);

	return null;
};

/** For authorized users (e.g. dashboard pages). */
export const ChatWidgetPrivate = () => {
	const environment = ENV_CONFIG.PROJECT_NAME_ENV;
	const { currentAccount } = useLoaderData();

	useChatWidgetScript();

	useEffect(() => {
		const chatWidgetScript = document.createElement("chat-widget") as ChatWidgetElement;

		const createToken = async (): Promise<string> => {
			const header = { alg: "HS256", typ: "JWT" };
			const payload = {
				email: `m.${currentAccount.userId}@${environment}.dreamcamtrue.com`,
				displayName: currentAccount.bss_username || "anonymous",
				iat: Math.floor(Date.now() / 1000),
				exp: Math.floor(Date.now() / 1000) + 60 * 5,
			};
			return signHs256Jwt(header, payload, ENV_CONFIG.JSM_SECRET_KEY ?? "");
		};

		const handleChatWidgetLoad = async () => {
			const token = await createToken();
			await chatWidgetScript.loginWithToken(token);
			chatWidgetScript.show();
		};

		setBaseAttributes(chatWidgetScript);
		chatWidgetScript.setAttribute("auto-show", "false");
		chatWidgetScript.setAttribute(
			"metadata",
			`[
				{ name: "Email", value: "${currentAccount.email}", render: true },
				{ name: "UserType", value: "Model", render: true },
				{ name: "Environment", value: "${environment}", render: true },
				{ name: "UserName", value: "${currentAccount.bss_username}", render: true }
			]`,
		);

		chatWidgetScript.onload = handleChatWidgetLoad;

		document.body.appendChild(chatWidgetScript);
		const removeStyles = appendChatWidgetStyles();

		return () => {
			document.body.removeChild(chatWidgetScript);
			chatWidgetScript.onload = null;
			removeStyles();
		};
	}, []);

	return null;
};
